import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const GuarantorModal = ({ isOpen, onClose, guarantor, potentialDriverId, onSave }) => {
    const { isDark } = useTheme();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        tin: '',
        id_type: '',
        id_number: '',
        address: '',
        digital_address: '',
        landmark: '',
        relationship_to_driver: '',
        status: '',
        potential_driver_id: potentialDriverId || ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        if (guarantor) {
            setFormData({
                first_name: guarantor.first_name || '',
                last_name: guarantor.last_name || '',
                phone_number: guarantor.phone_number || '',
                email: guarantor.email || '',
                tin: guarantor.tin || '',
                id_type: guarantor.id_type || '',
                id_number: guarantor.id_number || '',
                address: guarantor.address || '',
                digital_address: guarantor.digital_address || '',
                landmark: guarantor.landmark || '',
                relationship_to_driver: guarantor.relationship_to_driver || '',
                status: guarantor.status || '',
                potential_driver_id: guarantor.potential_driver_id || potentialDriverId || ''
            });
        } else {
            setFormData({
                first_name: '',
                last_name: '',
                phone_number: '',
                email: '',
                tin: '',
                id_type: '',
                id_number: '',
                address: '',
                digital_address: '',
                landmark: '',
                relationship_to_driver: '',
                status: '',
                potential_driver_id: potentialDriverId || ''
            });
        }
        setErrors([]);
    }, [guarantor, potentialDriverId, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors([]);

        try {
            const url = guarantor
                ? `/api/v1/guarantors/${guarantor.id}`
                : '/api/v1/guarantors';

            const method = guarantor ? 'PATCH' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
                body: JSON.stringify({ guarantor: formData }),
            });

            const data = await response.json();

            if (response.ok) {
                onSave(data);
                onClose();
            } else {
                setErrors(data.errors || ['An error occurred']);
            }
        } catch (error) {
            console.error('Error saving guarantor:', error);
            setErrors(['Failed to save guarantor']);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        modal: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '2rem',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
        },
        header: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
            marginBottom: '1.5rem',
        },
        errorContainer: {
            background: isDark ? '#7f1d1d' : '#fee2e2',
            color: isDark ? '#fca5a5' : '#991b1b',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1.5rem',
        },
        formGroup: {
            marginBottom: '1rem',
        },
        fullWidth: {
            gridColumn: '1 / -1',
        },
        label: {
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: isDark ? '#d1d5db' : '#374151',
            marginBottom: '0.5rem',
        },
        input: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#374151' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
        },
        buttonGroup: {
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            marginTop: '2rem',
            gridColumn: '1 / -1',
        },
        cancelButton: {
            padding: '0.75rem 1.5rem',
            background: isDark ? '#374151' : '#e5e7eb',
            color: isDark ? '#d1d5db' : '#374151',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
        saveButton: {
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h2 style={styles.header}>
                    {guarantor ? 'Edit Guarantor' : 'New Guarantor'}
                </h2>

                {errors.length > 0 && (
                    <div style={styles.errorContainer}>
                        {errors.map((error, index) => (
                            <div key={index}>{error}</div>
                        ))}
                    </div>
                )}

                <form onSubmit={handleSubmit} style={styles.grid}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>First Name *</label>
                        <input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            style={styles.input}
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Last Name *</label>
                        <input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            style={styles.input}
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Phone Number *</label>
                        <input
                            type="tel"
                            name="phone_number"
                            value={formData.phone_number}
                            onChange={handleChange}
                            style={styles.input}
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>TIN</label>
                        <input
                            type="text"
                            name="tin"
                            value={formData.tin}
                            onChange={handleChange}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>ID Type</label>
                        <select
                            name="id_type"
                            value={formData.id_type}
                            onChange={handleChange}
                            style={styles.input}
                        >
                            <option value="">Select ID Type</option>
                            <option value="Ghana Card">Ghana Card</option>
                            <option value="Voters ID">Voter's ID</option>
                            <option value="Passport">Passport</option>
                            <option value="Drivers License">Driver's License</option>
                            <option value="NHIS">NHIS</option>
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>ID Number</label>
                        <input
                            type="text"
                            name="id_number"
                            value={formData.id_number}
                            onChange={handleChange}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Relationship to Driver</label>
                        <input
                            type="text"
                            name="relationship_to_driver"
                            value={formData.relationship_to_driver}
                            onChange={handleChange}
                            style={styles.input}
                        />
                    </div>

                    <div style={{ ...styles.formGroup, ...styles.fullWidth }}>
                        <label style={styles.label}>Address</label>
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Digital Address</label>
                        <input
                            type="text"
                            name="digital_address"
                            value={formData.digital_address}
                            onChange={handleChange}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Landmark</label>
                        <input
                            type="text"
                            name="landmark"
                            value={formData.landmark}
                            onChange={handleChange}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            style={styles.saveButton}
                        >
                            {loading ? 'Saving...' : (guarantor ? 'Update' : 'Create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GuarantorModal;
